import MasqueradeBar from "@components/MasqueradeBar";
import Messenger from "@components/Messenger/Messenger";
import MobileRestricted from "@components/MobileRestricted";
import PrivateSponsorRoute from "@components/PrivateSponsorRoute";
import PylonChat from "@components/PylonChat";
import SitewideBanner from "@components/SitewideBanner";
import SubscriptionWarning from "@components/SubscriptionWarning";
import TaxonomyUpdateModal from "@components/TaxonomyUpdateModal";
import { Toaster } from "@components/Toast";
import toast from "@components/Toast/Toast";
import SSOSponsorOnboardingModal from "@components/modals/SSOSponsorOnboardingModal";
import { COLORS, FEATURE_FLAGS, JS_FONTS, WINDOW_DIMENSIONS } from "@constants";
import { fetchSubscription as fetchSubscriptionAction } from "@redux/actions/companyActions";
import { clearMessengerState as clearMessengerStateAction } from "@redux/actions/messengerActions";
import { DEFAULT_RETRY_COUNT } from "@requests/request";
import { signOut } from "@requests/users";
import SubmitProposalRedirect from "@routes/SubmitProposalRedirect";
import {
  discover_path,
  institution_directory_path,
  login_path,
  profile_page_path,
  solution_listing_path,
  university_path,
} from "@routes/routes";
import { marketplacePath } from "@routes/utils";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RequestableType } from "@tsTypes/requests";
import { UserRole } from "@tsTypes/users";
import ScrollToTop from "@utils/ScrollToTop";
import { identify, lazyWithRetry, page, setPageTitle } from "@utils/appUtils";
import { setPrevUrlCookie } from "@utils/authUtils";
import devConsole from "@utils/devConsole";
import { t } from "@utils/i18n";
import { consumeSignupPrefillParams } from "@utils/signupPrefill";
import { StyleSheet, css } from "aphrodite";
import axios, { AxiosError } from "axios";
import dayjs from "dayjs";
import { PureComponent, RefObject, Suspense, createRef } from "react";
import { connect } from "react-redux";
import { Redirect, Route, Switch } from "react-router-dom";
import { Loader } from "semantic-ui-react";
import FeatureFlagRoute from "./components/FeatureFlagRoute";
import Footer from "./components/Footer";
import Navbar from "./components/Navbar";
import PrivateRoute from "./components/PrivateRoute";
import {
  checkUnread as checkUnreadAction,
  fetchCurrentUser as fetchCurrentUserAction,
  setCurrentUser as setCurrentUserAction,
} from "./redux/actions/profileActions";
import { updateWindowDimension as updateWindowDimensionAction } from "./redux/actions/windowDimensionsActions";
import NotificationCampaignRoutes from "./routes/notification_campaign_routes";
import OpportunityRoutes from "./routes/opportunity_routes";
import ProposalPageRoutes from "./routes/proposal_page_routes";
import RfpRoutes from "./routes/rfp_routes";
import { request_for_expert_path } from "./routes/routes";
import { formatPathForRouter } from "./routes/utils";
import ErrorBoundary from "./views/ErrorBoundary";
import OnboardingContainer from "./views/Onboarding/OnboardingContainer";
import Unauthorized401 from "./views/StatusPages/401";
import Forbidden403 from "./views/StatusPages/403";
import NotFound404 from "./views/StatusPages/404";

const RfePage = lazyWithRetry(() => import("./views/RequestForExpertsPage"));
const RequestForStartupPage = lazyWithRetry(() => import("./views/RequestForStartupPage"));
const CompanyPage = lazyWithRetry(() => import("./views/CompanyPage"));
const ProfilePage = lazyWithRetry(() => import("./views/ProfilePage"));
const InnovatorPage = lazyWithRetry(() => import("./views/InnovatorPage"));
const ReviewerDashboard = lazyWithRetry(() => import("./views/ReviewerDashboard"));
const ProposalReviewSystem = lazyWithRetry(() => import("./views/ProposalReviewSystem"));
const Account = lazyWithRetry(() => import("./views/Account"));
const RfpOpenCallProposalForm = lazyWithRetry(() => import("./views/RfpOpenCallProposalForm"));
const RequestForStartupProposalForm = lazyWithRetry(
  () => import("./views/RequestForStartupProposalForm")
);
const RequestForExpertProposalForm = lazyWithRetry(
  () => import("./views/RequestForExpertProposalForm")
);
const CommercialApplicationPage = lazyWithRetry(() => import("./views/CommercialApplicationPage"));
const Marketplace = lazyWithRetry(() => import("./views/Marketplace"));
const TermsPage = lazyWithRetry(() => import("./views/TermsPage"));
const PrivacyPage = lazyWithRetry(() => import("./views/PrivacyPage"));
const ContactPage = lazyWithRetry(() => import("./views/ContactPage"));
const SponsorTeam = lazyWithRetry(() => import("./views/SponsorTeam"));
const ForgotPassword = lazyWithRetry(() => import("./views/ForgotPassword"));
const ChangePassword = lazyWithRetry(() => import("./views/ChangePassword"));
const ScientistProposalLibrary = lazyWithRetry(() => import("./views/ScientistProposalLibrary"));
const Careers = lazyWithRetry(() => import("./views/Careers"));
const Inbox = lazyWithRetry(() => import("./views/Inbox"));
const ProposalLibrary = lazyWithRetry(() => import("./views/ProposalLibrary"));
const ScientistNetwork = lazyWithRetry(() => import("./views/ScientistNetwork"));
const Discover = lazyWithRetry(() => import("./views/Discover"));
const SolutionListingPage = lazyWithRetry(() => import("./views/SolutionListingPage"));
const AboutUs = lazyWithRetry(() => import("./views/AboutUs"));
const LoginPage = lazyWithRetry(() => import("./views/LoginPage"));
const SignUp = lazyWithRetry(() => import("./views/SignUp/SignUp"));
const ScientistNetworkSignUp = lazyWithRetry(() => import("./views/ScientistNetworkSignUp"));
const RecommendedRfpSignUp = lazyWithRetry(() => import("./views/RecommendedRfpSignUp"));
const UniversityProposalLibrary = lazyWithRetry(() => import("./views/UniversityProposalLibrary"));
const UniversityTeam = lazyWithRetry(() => import("./views/UniversityTeam"));
const FeatureRequestSplashPage = lazyWithRetry(() => import("./views/FeatureRequestSplashPage"));
const InstitutionDirectory = lazyWithRetry(
  () => import("./views/InstitutionDirectory/InstitutionDirectory")
);
const InstitutionPage = lazyWithRetry(() => import("./views/InstitutionPage/InstitutionPage"));

const { TABLET_MEDIA_QUERY } = WINDOW_DIMENSIONS;

export enum RequestType {
  REQUEST_FOR_PROPOSAL = "request_for_proposal",
  OPEN_CALL = "open_call",
  REQUEST_FOR_STARTUP = "request_for_startup",
  REQUEST_FOR_EXPERT = "request_for_experts",
}

export const REQUESTABLE_TO_REQUEST_TYPE = {
  [RequestableType.RFP]: RequestType.REQUEST_FOR_PROPOSAL,
  [RequestableType.OPEN_CALL]: RequestType.OPEN_CALL,
  [RequestableType.REQUEST_FOR_STARTUP]: RequestType.REQUEST_FOR_STARTUP,
  [RequestableType.REQUEST_FOR_EXPERT]: RequestType.REQUEST_FOR_EXPERT,
};

// TODO: (overload119) Add correct typing.
type Props = any;
interface State {
  isTaxonomyUpdateModalOpen: boolean;
  isSSOSponsorOnboardingModalOpen: boolean;
  isCurrentUserLoading: boolean;
  isWebflow: boolean;
}

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Skip if the mutation has its own error handler
      if (mutation.options.onError) return;

      const meta = mutation.meta;
      if (!meta?.useGlobalErrorHandler || !axios.isAxiosError(error)) {
        return;
      }

      const errorMessage = error.response?.data?.error ?? error.response?.data.errors;
      if (Array.isArray(errorMessage)) {
        toast.error(
          <div className="with-list">
            The following errors prevented that action:
            <ul>
              {errorMessage.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        );
      } else if (errorMessage) {
        toast.error(errorMessage);
      } else if (error.response?.status === 401) {
        toast.error(
          <div>
            You have to be logged in to do that. <a href={login_path()}>Click here to log in</a>
          </div>
        );
      } else if (error.response?.status === 403) {
        toast.error("You don't have permission to do that.");
      } else {
        const status = error?.response?.status;
        toast.error(
          t(["error", status ? "default_with_code" : "default"], { status: status ?? 500 })
        );
      }
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (
          (error as any).isAxiosError &&
          [401, 403, 404].includes((error as AxiosError).response?.status ?? 0)
        ) {
          return false;
        }
        return failureCount < DEFAULT_RETRY_COUNT;
      },
    },
  },
});

const TIMEOUT_IN_MS = 900000; // 15 minutes
let sessionTimer: null | number = null;

const isPageRequiringReload = (url: string): boolean => {
  return url.startsWith("/scientists");
};

class App extends PureComponent<Props, State> {
  listener: (() => void) | null;
  webflowContainerRef: RefObject<HTMLDivElement>;

  constructor(props) {
    super(props);

    this.listener = null;
    this.webflowContainerRef = createRef();
    this.state = {
      isTaxonomyUpdateModalOpen: false,
      isSSOSponsorOnboardingModalOpen: false,
      isCurrentUserLoading: true,
      isWebflow: false,
    };

    axios.defaults.headers.common.Accept = "application/json";
    axios.defaults.headers.common["Content-Type"] = "application/json";
  }

  async componentDidMount() {
    const {
      updateWindowDimension,
      fetchCurrentUser,
      clearMessengerState,
      fetchSubscription,
      history,
    } = this.props;

    // Consume signup prefill URL params on app load (removes them from URL, stores in localStorage)
    consumeSignupPrefillParams();

    this.listener = history.listen((location, action: "POP" | "PUSH") => {
      if (action === "POP" && isPageRequiringReload(location.pathname)) {
        window.location.reload();
      }
    });

    page();

    // Listen for window resizing, and update `windowDimension` in Redux
    window.addEventListener("resize", updateWindowDimension);

    if (window.outerWidth > 0 && window.outerHeight > 0) {
      // Sometimes window hasn't fully loaded yet so we shouldn't set to 0 in those
      // cases
      updateWindowDimension();
    }

    const user = await fetchCurrentUser(true);

    if (!user.id) {
      clearMessengerState();
    } else {
      // Fetch subscription info if user is a sponsor
      if (user.role === UserRole.SPONSOR) {
        fetchSubscription(user.profile_info.company_identifier);
      }

      const wasUserCreatedMoreThan1DayAgo = dayjs().diff(dayjs(user.created_at), "days") > 1;
      const isSponsorUserMissingTitleAndRole =
        user?.role === UserRole.SPONSOR &&
        user.profile_info &&
        !user.profile_info.title &&
        !user.profile_info.role;

      this.setState({
        isTaxonomyUpdateModalOpen:
          !location.pathname.startsWith("/onboarding") &&
          !location.pathname.startsWith("/contact") &&
          user?.role !== UserRole.UNIVERSITY_ADMIN &&
          user?.has_updated_taxonomy === false,
        isSSOSponsorOnboardingModalOpen:
          !location.pathname.startsWith("/onboarding") &&
          !location.pathname.startsWith("/network") &&
          !location.pathname.startsWith("/contact") &&
          wasUserCreatedMoreThan1DayAgo &&
          isSponsorUserMissingTitleAndRole,
      });
    }

    this.setState({
      isCurrentUserLoading: false,
    });

    // Move webflow content into the webflow container to place between navbar and footer
    const webflowContent = document.getElementById("webflow-content");
    if (webflowContent) {
      const webflowContainer = this.webflowContainerRef.current;
      webflowContainer?.appendChild(webflowContent);

      // Display the webflow content after moving to prevent initial flashing
      webflowContent.style.display = "block";
      this.setState({ isWebflow: Boolean(webflowContainer?.children.length) });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props?.currentUser) {
      identify(this.props.currentUser);

      // NOTE: (overload119) An idle page will
      // auto-logout if this feature is enabled.
      if (this.props.currentUser?.id && FEATURE_FLAGS.SESSION_TIMEOUT) {
        // Prevent more than 1 instance of the timeout.
        if (sessionTimer !== null) {
          clearTimeout(sessionTimer);
        }
        sessionTimer = setTimeout(async () => {
          await signOut();
          toast.error("You have been signed out due to inactivity.");
          clearTimeout(sessionTimer as number);
          sessionTimer = null;
        }, TIMEOUT_IN_MS) as unknown as number;
      }
    }

    if (this.props.location !== prevProps.location) {
      page();

      if (this.props.currentUser) this.props.checkUnread();
    }
  }

  componentWillUnmount() {
    const { updateWindowDimension } = this.props;
    window.removeEventListener("resize", updateWindowDimension);
    this.listener?.();
  }

  render() {
    const { location, currentUser } = this.props;

    setPageTitle(location.pathname);

    const { isWebflow } = this.state;

    const noFooter =
      location.pathname.includes("/submit_proposal") ||
      location.pathname.startsWith("/opportunities") ||
      location.pathname.startsWith("/university_dashboard") ||
      location.pathname.startsWith("/submit/") ||
      location.pathname.startsWith("/inbox") ||
      location.pathname.startsWith("/login") ||
      location.pathname.startsWith("/get-started") ||
      location.pathname.startsWith("/preview-rfp/") ||
      location.pathname.startsWith("/onboarding") ||
      location.pathname.includes("/signup") ||
      location.pathname.startsWith("/review_system");

    const noChat =
      !currentUser.id ||
      location.pathname.includes("/submit_proposal") ||
      location.pathname.startsWith("/submit/") ||
      location.pathname.startsWith("/preview-rfp/") ||
      location.pathname.startsWith("/onboarding") ||
      location.pathname.includes("/signup");

    // Track original referrer id
    if (localStorage && !localStorage.getItem("original_referrer_id")) {
      const urlParams = new URLSearchParams(window.location.search);
      const referrer_id = urlParams.get("referrer_id");

      if (referrer_id) {
        localStorage.setItem("original_referrer_id", referrer_id);
      }
    }

    // HACK: Temporary fix for unsubscribe page not working.
    // Remove this later and rethink this statement.
    if (
      (!location.pathname.startsWith("/contact") &&
        !location.pathname.startsWith("/unsubscribe") &&
        !gon.current_user_id &&
        currentUser.id) ||
      this.state.isCurrentUserLoading
    ) {
      return <Loader style={{ marginTop: "30vh" }} active inline="centered" />;
    }

    let homepage = marketplacePath();
    if (currentUser.role === UserRole.SPONSOR) {
      if (currentUser.profile_info.commenter) homepage = "/reviewer_dashboard/proposals";
      else if (FEATURE_FLAGS.DISCOVER_FEED) homepage = discover_path();
      else homepage = "/reviewer_dashboard";
    }
    if (currentUser.role === UserRole.UNIVERSITY_ADMIN) homepage = "/university_dashboard/team";

    return (
      <QueryClientProvider client={queryClient}>
        {gon.user_masquerade && <MasqueradeBar />}
        {gon.sitewide_banner && <SitewideBanner />}
        <ErrorBoundary>
          <div className={css(styles.Marketplace_app)}>
            <div
              data-test-profile-id={currentUser.profile_id}
              data-testid="marketplace-container-main"
              className={css(
                isWebflow ? null : styles.Marketplace_mainContainer,
                noFooter ? styles.Marketplace_noFooter : null
              )}
            >
              {!noChat && <Messenger />}
              <PylonChat />
              <Navbar />
              {currentUser.role === UserRole.SPONSOR && <SubscriptionWarning />}
              <ScrollToTop>
                <Suspense
                  fallback={
                    <Loader
                      style={{
                        marginTop: "200px",
                        height: "500px",
                        alignItems: "center",
                        justifyContent: "center",
                        display: "flex",
                      }}
                      active
                      inline="centered"
                    />
                  }
                >
                  {this.state.isTaxonomyUpdateModalOpen && (
                    <TaxonomyUpdateModal
                      isOpen={this.state.isTaxonomyUpdateModalOpen}
                      onClose={() => this.setState({ isTaxonomyUpdateModalOpen: false })}
                    />
                  )}
                  {this.state.isSSOSponsorOnboardingModalOpen && (
                    <SSOSponsorOnboardingModal
                      isOpen={this.state.isSSOSponsorOnboardingModalOpen}
                      onClose={() => this.setState({ isSSOSponsorOnboardingModalOpen: false })}
                    />
                  )}
                  <Switch>
                    {currentUser.id && (
                      <Route exact path="/">
                        <Redirect to={homepage} />
                      </Route>
                    )}
                    <Route exact path="/about" component={AboutUs} />
                    <Route exact path="/home" component={Marketplace} />
                    <PrivateRoute exact path="/my_proposals" component={ScientistProposalLibrary} />
                    <Route exact path="/login" component={LoginPage} />
                    <Redirect from="/register" to="/get-started" />
                    <Route exact path="/get-started/network" component={ScientistNetworkSignUp} />
                    {currentUser.id ? (
                      <Route exact path="/get-started/keywords">
                        <Redirect to={profile_page_path(currentUser.profile_id, { keywords: 1 })} />
                      </Route>
                    ) : (
                      <Route
                        exact
                        path="/get-started/keywords"
                        render={() => {
                          setPrevUrlCookie("/get-started/keywords");
                          return <Redirect to="/login" />;
                        }}
                      />
                    )}
                    <Route
                      exact
                      path="/get-started/opportunities"
                      component={RecommendedRfpSignUp}
                    />
                    <Route exact path="/get-started" component={SignUp} />
                    {/* Regular onboarding */}
                    <PrivateRoute exact path="/onboarding" component={OnboardingContainer} />
                    <Route exact path="/submit/:request_type/:slug">
                      {({ match }) => {
                        const { request_type } = match.params;
                        switch (request_type) {
                          case RequestType.REQUEST_FOR_PROPOSAL:
                          case RequestType.OPEN_CALL:
                            return <RfpOpenCallProposalForm />;
                          case RequestType.REQUEST_FOR_STARTUP:
                            return <RequestForStartupProposalForm />;
                          case RequestType.REQUEST_FOR_EXPERT:
                            return <RequestForExpertProposalForm />;
                          default:
                            devConsole.error("Unexpected request_type: %s.", request_type);
                        }
                      }}
                    </Route>
                    <Route exact path="/submit_proposal/:rfp_identifier">
                      <SubmitProposalRedirect />
                    </Route>
                    <Route exact path="/company/:identifier" component={CompanyPage} />
                    <FeatureFlagRoute
                      exact
                      path="/profile/:id"
                      featureFlag={FEATURE_FLAGS.INNOVATOR_PROFILE_PAGE}
                      enabledComponent={InnovatorPage}
                      disabledComponent={ProfilePage}
                    />
                    <Route
                      exact
                      path="/innovator/:slug"
                      render={({ match }) => <Redirect to={`/profile/${match.params.slug}`} />}
                    />
                    <PrivateSponsorRoute
                      exact
                      path="/reviewer_dashboard"
                      component={ReviewerDashboard}
                    />
                    <PrivateRoute
                      exact
                      path="/reviewer_dashboard/proposals"
                      render={({ location: _location }) => (
                        <Redirect to={{ ..._location, pathname: "/review_system" }} />
                      )}
                    />
                    <PrivateRoute path="/review_system" exact component={ProposalReviewSystem} />
                    <Redirect path="/expert_dashboard" to="/review_system" />
                    <PrivateRoute
                      exact
                      path="/reviewer_dashboard/teams"
                      render={() => (
                        <MobileRestricted breakpoint="500px">
                          <SponsorTeam />
                        </MobileRestricted>
                      )}
                    />
                    <PrivateRoute
                      exact
                      path="/university_dashboard/proposals"
                      component={UniversityProposalLibrary}
                    />
                    <PrivateRoute
                      exact
                      path="/university_dashboard/team"
                      component={UniversityTeam}
                    />
                    <PrivateRoute exact path="/account" component={Account} />
                    <Route
                      exact
                      path={formatPathForRouter(request_for_expert_path)}
                      component={RfePage}
                    />
                    <Route exact path="/startup_program/:slug" component={RequestForStartupPage} />
                    {ProposalPageRoutes}
                    <PrivateRoute exact path="/proposal_library" component={ProposalLibrary} />
                    {RfpRoutes}
                    {NotificationCampaignRoutes}
                    {OpportunityRoutes}
                    <PrivateSponsorRoute path={discover_path()} component={Discover} />
                    <PrivateRoute
                      exact
                      path={formatPathForRouter(solution_listing_path)}
                      component={SolutionListingPage}
                    />
                    <Route exact path="/network" component={ScientistNetwork} />
                    <Route
                      exact
                      path="/application/:commercial_application"
                      component={CommercialApplicationPage}
                    />
                    <Route exact path="/terms" component={TermsPage} />
                    <Route exact path="/privacy" component={PrivacyPage} />
                    <Route exact path="/contact" component={ContactPage} />
                    <PrivateRoute exact path="/inbox" component={Inbox} />
                    <Route exact path="/forgot_password" component={ForgotPassword} />
                    <Route exact path="/change_password" component={ChangePassword} />
                    <Route exact path="/careers" component={Careers} />
                    <Route exact path="/sponsors">
                      <Redirect to="/marketplace/partners" />
                    </Route>
                    <Route path={"/marketplace"} component={Marketplace} />
                    <Route exact path="/feature_requests" component={FeatureRequestSplashPage} />

                    <Route
                      exact
                      path={institution_directory_path()}
                      component={InstitutionDirectory}
                    />
                    <Route
                      exact
                      path={formatPathForRouter(university_path)}
                      component={InstitutionPage}
                    />

                    {isWebflow && <Route exact path={window.location.pathname} />}

                    <Route exact path="/401" component={Unauthorized401} />
                    <Route exact path="/403" component={Forbidden403} />
                    <Route exact path="/404" component={NotFound404} />
                    <Route path="*" component={NotFound404} />
                  </Switch>
                </Suspense>
              </ScrollToTop>
            </div>
          </div>
          <div id="webflow-container" ref={this.webflowContainerRef} />
          <Toaster />
          {!noFooter && <Footer />}
        </ErrorBoundary>
        {!import.meta.env.CYPRESS && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    );
  }
}

const mapStateToProps = (state) => ({
  currentUser: state.profiles.currentUser,
  windowWidth: state.window.width,
});

const mapDispatchToProps = {
  updateWindowDimension: updateWindowDimensionAction,
  fetchCurrentUser: fetchCurrentUserAction,
  setCurrentUser: setCurrentUserAction,
  checkUnread: checkUnreadAction,
  clearMessengerState: clearMessengerStateAction,
  fetchSubscription: fetchSubscriptionAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);

const styles = StyleSheet.create({
  Marketplace_app: {
    position: "relative",
    display: "grid",
    height: "fit-content",
    gridTemplateColumns: "100%",
  },
  Marketplace_mainContainer: {
    display: "inline-block",
    position: "relative",
    minHeight: "100vh",
    marginBottom: "40px",
  },
  Marketplace_mobileContainer: {
    [TABLET_MEDIA_QUERY]: {
      ":nth-child(1n) > .ui.container": {
        marginLeft: "unset",
        marginRight: "unset",
        paddingLeft: "unset",
        paddingRight: "unset",
      },
    },
  },
  Marketplace_noFooter: {
    marginBottom: "0px",
  },
  toast_body: {
    color: COLORS.WHITE,
    marginRight: "10px",
    ...JS_FONTS.MEDIUM_3,
  },
  toast_progress: {
    background: COLORS.HALO_BLUE,
  },
  toast_inner: {
    padding: "15px",
    paddingBottom: "20px",
    borderRadius: "4px",
  },
  toast_container: {
    zIndex: 1000000,
  },
  toast_close: {
    position: "initial",
  },
});
